/**
 * Impersonation banner — persistent red bar shown across every page while an
 * admin is impersonating a user. Auto-expires after 30 minutes.
 *
 * Legal/compliance table-stakes: the impersonating admin must always be aware
 * they are acting as someone else, and the session must hard-cap.
 *
 * Contract:
 *   - To start an impersonation session, call `startImpersonation({ userId,
 *     userName })` from anywhere (e.g. ImpersonateUserPage). This writes a
 *     record to localStorage.
 *   - Mount `<ImpersonationBanner />` once near the app root. It reads the
 *     record and renders the bar on every page.
 *   - "End impersonation" or the 30-minute timer both clear the record and
 *     write an audit entry.
 */

import React, { useCallback, useEffect, useState } from "react";
import { appendAudit } from "./opsShared";

const LS_KEY = "opsdash.impersonation.v1";
const SESSION_MS = 30 * 60 * 1000; // 30 minutes

// ---- helpers ----

const readSession = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const writeSession = (session) => {
  try {
    if (session) {
      localStorage.setItem(LS_KEY, JSON.stringify(session));
    } else {
      localStorage.removeItem(LS_KEY);
    }
  } catch {
    // ignore
  }
};

export const startImpersonation = ({ userId, userName }) => {
  const session = {
    userId,
    userName,
    startedAt: new Date().toISOString(),
    expiresAt: new Date(Date.now() + SESSION_MS).toISOString(),
  };
  writeSession(session);
  appendAudit({
    area: "impersonation",
    action: "start",
    subjectId: userId,
    detail: `Started impersonating ${userName}`,
  });
  // Notify banners in other tabs / this tab
  window.dispatchEvent(new Event("opsdash:impersonation-changed"));
  return session;
};

export const endImpersonation = (reason = "manual") => {
  const session = readSession();
  if (session) {
    appendAudit({
      area: "impersonation",
      action: "end",
      subjectId: session.userId,
      detail: `Ended impersonation of ${session.userName} (${reason})`,
    });
  }
  writeSession(null);
  window.dispatchEvent(new Event("opsdash:impersonation-changed"));
};

// ---- banner component ----

const formatRemaining = (ms) => {
  if (ms <= 0) return "0:00";
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

export const ImpersonationBanner = () => {
  const [session, setSession] = useState(() => readSession());
  const [now, setNow] = useState(() => Date.now());

  // Poll storage + tick every second
  useEffect(() => {
    const refresh = () => setSession(readSession());
    const tick = () => setNow(Date.now());
    const interval = setInterval(() => {
      refresh();
      tick();
    }, 1000);
    window.addEventListener("opsdash:impersonation-changed", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener("opsdash:impersonation-changed", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  // Auto-expire
  useEffect(() => {
    if (!session) return;
    const expires = new Date(session.expiresAt).getTime();
    if (now >= expires) {
      endImpersonation("auto_expired");
    }
  }, [session, now]);

  const handleEnd = useCallback(() => {
    endImpersonation("manual");
  }, []);

  if (!session) return null;
  const remaining = new Date(session.expiresAt).getTime() - now;
  const pct = Math.max(0, Math.min(100, (remaining / SESSION_MS) * 100));

  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{ position: "sticky", top: 0, zIndex: 9999 }}
      className="w-full bg-red-600 text-white shadow-lg"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-3 min-w-0">
          <span aria-hidden>⚠️</span>
          <span className="truncate">
            <strong>Impersonation active</strong> — acting as{" "}
            <strong>{session.userName}</strong>. All actions are audited.
          </span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="font-mono tabular-nums">
            {formatRemaining(remaining)} left
          </span>
          <button
            type="button"
            onClick={handleEnd}
            className="px-3 py-1 rounded bg-white text-red-700 font-semibold hover:bg-red-50"
          >
            End now
          </button>
        </div>
      </div>
      <div className="h-0.5 bg-red-800">
        <div
          className="h-full bg-white transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

export default ImpersonationBanner;
